import compactStyles from '../../../components/trading/CompactTable.module.css';
import { PositionSnapshot, StrategyPositionSnapshot } from '../../../types/trading';

/* ── Display formatters ───────────────────────────────────────────── */

export const fmtPrice = (v: number | null | undefined): string => {
  if (typeof v !== 'number' || Number.isNaN(v)) return '—';
  return v.toFixed(2);
};

export const fmtCurrency = (v: number | null | undefined): string => {
  if (typeof v !== 'number' || Number.isNaN(v)) return '—';
  return v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export const fmtReturnPct = (v: number | null | undefined): string => {
  if (typeof v !== 'number' || Number.isNaN(v)) return '—';
  return `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`;
};

export const fmtPct = (v: number): string =>
  `${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;

export const fmtPnl = (v: number): string => {
  const s = Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return v >= 0 ? s : `-${s}`;
};

export const fmtDate = (d: string): string => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return d; }
};

export const fmtTime = (value: string | null | undefined): string => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
};

/* ── Numeric helpers ──────────────────────────────────────────────── */

export const toFiniteNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parsed = Number(trimmed);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
};

/* ── PnL CSS class ────────────────────────────────────────────────── */

export const pnlClass = (value: number | null): string => {
  if (typeof value !== 'number') return '';
  if (value > 0) return compactStyles.pnlPositive;
  if (value < 0) return compactStyles.pnlNegative;
  return '';
};

export const pnlStyle = (value: number | null | undefined): React.CSSProperties => {
  if (typeof value !== 'number') return {};
  return value < 0
    ? { color: '#ef4444', fontWeight: 600 }
    : { color: '#22c55e', fontWeight: 600 };
};

/* ── Sort helpers ─────────────────────────────────────────────────── */

export const sortPositionsByTradeAtDesc = (items: PositionSnapshot[]): PositionSnapshot[] =>
  [...items].sort((a, b) => {
    const safeA = Number.isNaN(Date.parse(a.trade_at ?? '')) ? 0 : Date.parse(a.trade_at ?? '');
    const safeB = Number.isNaN(Date.parse(b.trade_at ?? '')) ? 0 : Date.parse(b.trade_at ?? '');
    return safeA !== safeB ? safeB - safeA : a.lot_id.localeCompare(b.lot_id);
  });

export const sortStrategyPositionsByUpdatedAtDesc = (items: StrategyPositionSnapshot[]): StrategyPositionSnapshot[] =>
  [...items].sort((a, b) => {
    const safeA = Number.isNaN(Date.parse(a.updated_at ?? '')) ? 0 : Date.parse(a.updated_at ?? '');
    const safeB = Number.isNaN(Date.parse(b.updated_at ?? '')) ? 0 : Date.parse(b.updated_at ?? '');
    return safeA !== safeB ? safeB - safeA : a.name.localeCompare(b.name);
  });

/* ── Misc helpers ─────────────────────────────────────────────────── */

export const removeStrategyPosition = (positions: StrategyPositionSnapshot[], id: string): StrategyPositionSnapshot[] =>
  positions.filter((p) => p.strategy_position_id !== id);

export const toBacktestRequest = <T>(config: Record<string, unknown> | null | undefined): T | null => {
  if (!config || typeof config !== 'object') return null;
  return config as unknown as T;
};

export const errorMessage = (reason: unknown, fallback: string): string => {
  if (reason && typeof reason === 'object' && 'message' in reason) {
    const { message } = reason as { message?: unknown };
    if (typeof message === 'string') return message;
  }
  return fallback;
};
